import { FEATURE } from '@src/constants/package';
import { AUDIO_STATUS } from '@src/constants/professionalVoiceCloneUpload';
import {
  AUDIO_STATUSES,
  CATEGORIES,
  NEW_VOICE_SUB_STATUSES,
  VOICE_CLONE_TYPE,
  VOICE_STATUSES,
} from '@src/constants/voiceCloning';
import { uploadFileToS3 } from '@src/services/upload';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

const getCategoryLabel = (value, locale) => {
  const category = CATEGORIES.find((item) => item.value === value);
  if (locale === 'en') return category ? category.labelEn : value;
  return category ? category.label : value;
};

const getAudioStatus = (value) => {
  const status = AUDIO_STATUSES.find((item) => item.value === value);
  return status ? status.label : value;
};

const getVoiceStatusLabel = (value) => {
  const voice = VOICE_STATUSES.find((item) => item.value === value);

  return voice ? voice.label : value;
};

const validateUserPackageCanUseVoiceCloning = ({
  ttsUser,
  voice,
  isUserInWhiteList,
}) => {
  if (!ttsUser) return false;
  if (isUserInWhiteList) return true;

  // Valid user: package not expired (Free user still pass this)
  const isPackageExpired =
    ttsUser?.packageExpiryDate && moment().isAfter(ttsUser?.packageExpiryDate);
  if (isPackageExpired) return false;

  // TODO: Validate by features in ttsUser
  if (voice?.type === VOICE_CLONE_TYPE.ZERO_SHOT) return true;

  return ttsUser?.features?.includes(FEATURE.AI_VOICE_CLONING);
};

const validateUserCanUseInstantVoiceCloning = ({ ttsUser }) => {
  if (!ttsUser) return false;
  const isPackageExpired =
    ttsUser?.packageExpiryDate && moment().isAfter(ttsUser?.packageExpiryDate);
  if (isPackageExpired) return false;

  return ttsUser?.features?.includes(FEATURE.AI_INSTANT_VOICE_CLONING);
};

const getVoiceSubStatusLabel = (value) => {
  const voice = NEW_VOICE_SUB_STATUSES.find((item) => item.value === value);

  return voice ? voice.label : value;
};

const getFrameVoiceUrl = (voice) => {
  const expiresAt = voice?.badge?.expiresAt;
  if (!expiresAt || (expiresAt && moment().isAfter(expiresAt))) return null;
  if (voice?.badge?.imageUrl) return voice?.badge?.imageUrl;
  return null;
};

const getS3Url = async ({ fileUrl }) => {
  if (!fileUrl) return null;
  const audioId = uuidv4();

  const handleUploadAudioToS3 = async () => {
    const response = await fetch(fileUrl);
    const file = await response.blob();
    const extension = 'wav';

    const name = `audio-${audioId}`;
    const directory = `voice-cloning/voices/${audioId}`;
    const s3Url = await uploadFileToS3(name, directory, extension, file);
    return s3Url;
  };

  const s3Url = await handleUploadAudioToS3();

  return s3Url;
};

/**
 * Calculate quality percent from audio quality data
 * @param {Object} quality - Quality object with acceptedPercent
 * @param {Boolean} denoise - Whether audio was processed with denoise
 * @returns {Number} Quality percent (0-100)
 */
const calculateQualityPercent = (quality, denoise = false) => {
  if (!quality?.acceptedPercent) return 0;

  const { acceptedPercent } = quality;

  if (denoise) {
    return acceptedPercent.withDenoise || 0;
  }

  return acceptedPercent.original || 0;
};

/**
 * Create a Map from audios array
 * @param {Array} audios - Array of audio objects with id
 * @returns {Map} Map with audio id as key
 */
const createAudioQualityMap = (audios) =>
  new Map(audios.map((audio) => [audio.id, audio]));

/**
 * Process quality updates from API response
 * Returns audio updates object with quality data
 * @param {Array} audiosWithQuality - Array of audios with quality data from API
 * @param {Array} checkingIds - Array of audio IDs that are being checked
 * @returns {Object} Object with audio updates { audioId: { quality, qualityPercent } }
 */
const processQualityUpdates = (audiosWithQuality, checkingIds) => {
  if (!Array.isArray(audiosWithQuality) || !Array.isArray(checkingIds)) {
    return {};
  }

  // Create a Map for O(1) lookup instead of O(n) find
  const qualityMap = createAudioQualityMap(audiosWithQuality);

  // Process checking IDs and build updates object
  const audioUpdates = checkingIds.reduce((acc, serverAudioId) => {
    const audioWithQuality = qualityMap.get(serverAudioId);

    if (audioWithQuality?.quality?.acceptedPercent) {
      const qualityPercent = calculateQualityPercent(
        audioWithQuality.quality,
        audioWithQuality.denoise,
      );

      return {
        ...acc,
        [serverAudioId]: {
          quality: audioWithQuality.quality,
          qualityPercent,
        },
      };
    }

    return acc;
  }, {});

  return audioUpdates;
};

/**
 * Update audio objects with quality data
 * @param {Array} audios - Array of audio objects
 * @param {Object} audioUpdates - Object with quality updates { audioId: { quality, qualityPercent } }
 * @returns {Array} Updated array of audio objects
 */
const updateAudiosWithQuality = ({ audios, audioUpdates }) => {
  if (
    !Array.isArray(audios) ||
    !audioUpdates ||
    Object.keys(audioUpdates).length === 0
  ) {
    return audios;
  }

  return audios.map((audio) => {
    const update = audioUpdates[audio.id];
    if (update) {
      return {
        ...audio,
        status: AUDIO_STATUS.SUCCESS,
        quality: update.quality,
        qualityImprovement: update.qualityPercent,
      };
    }
    return audio;
  });
};

/**
 * Mark audios as error based on checking IDs
 * @param {Array} audios - Array of audio objects
 * @param {Set|Array} checkingIds - Set or Array of audio IDs to mark as error
 * @param {String} errorMessage - Error message to set
 * @returns {Array} Updated array of audio objects
 */
const markAudiosAsError = ({ audios, checkingIds, errorMessage }) => {
  if (!Array.isArray(audios)) {
    return audios;
  }

  const checkingIdsSet =
    checkingIds instanceof Set ? checkingIds : new Set(checkingIds);

  return audios.map((audio) => {
    if (checkingIdsSet.has(audio.id)) {
      return {
        ...audio,
        status: AUDIO_STATUS.ERROR,
        errorMessage,
      };
    }
    return audio;
  });
};

/**
 * Filter audio IDs that have quality data from API response
 * @param {Array} audiosWithQuality - Array of audios with quality data from API
 * @param {Array} checkingIds - Array of audio IDs that are being checked
 * @returns {Array} Array of audio IDs that have quality data
 */
const getAudioIdsWithQuality = (audiosWithQuality, checkingIds) => {
  if (!Array.isArray(audiosWithQuality) || !Array.isArray(checkingIds)) {
    return [];
  }

  const qualityMap = createAudioQualityMap(audiosWithQuality);

  return checkingIds.filter((audioId) => {
    const audio = qualityMap.get(audioId);
    return audio?.quality?.acceptedPercent;
  });
};

export {
  getCategoryLabel,
  getAudioStatus,
  getVoiceStatusLabel,
  validateUserPackageCanUseVoiceCloning,
  validateUserCanUseInstantVoiceCloning,
  getVoiceSubStatusLabel,
  getFrameVoiceUrl,
  getS3Url,
  calculateQualityPercent,
  createAudioQualityMap,
  processQualityUpdates,
  updateAudiosWithQuality,
  markAudiosAsError,
  getAudioIdsWithQuality,
};
